import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAllArticles, deleteArticle, updateArticle } from '../../firebase/firestore';
import { deleteFile } from '../../firebase/storage';
import { formatDateTime } from '../../utils/dateHelpers';
import './ManageArticles.css';

export default function ManageArticles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    const data = await getAllArticles();
    setArticles(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    if (article.thumbnailURL) await deleteFile(article.thumbnailURL).catch(() => { });
    await deleteArticle(article.id);
    setArticles(prev => prev.filter(a => a.id !== article.id));
  };

  const handleToggleStatus = async (article) => {
    const newStatus = article.status === 'published' ? 'draft' : 'published';
    await updateArticle(article.id, { status: newStatus });
    setArticles(prev => prev.map(a => a.id === article.id ? { ...a, status: newStatus } : a));
  };

  const filtered = articles.filter(a => {
    const matchStatus = filter === 'all' || a.status === filter;
    const matchSearch = !search || a.title?.toLowerCase().includes(search.toLowerCase())
      || a.category?.toLowerCase().includes(search.toLowerCase())
      || a.author?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  return (
    <div className="manage-articles">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">📰 Manage Articles</h1>
          <p className="page-subtitle">{articles.length} total · {articles.filter(a => a.status === 'published').length} published · {articles.filter(a => a.status === 'draft').length} drafts</p>
        </div>
        <Link to="/admin/add-article" className="btn btn-primary">✏️ Write New Article</Link>
      </div>

      <div className="articles-controls">
        <div className="filter-tabs">
          {['all', 'published', 'draft'].map(f => (
            <button key={f} className={`filter-tab ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f === 'published' ? '✅ Published' : '📝 Drafts'}
            </button>
          ))}
        </div>
        <input
          className="articles-search"
          type="text"
          placeholder="Search articles..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? <div className="loading-spinner">Loading articles...</div>
        : filtered.length === 0
          ? <div className="empty-state"><h3>No articles found</h3></div>
          : (
            <div className="articles-table-wrap">
              <table className="articles-table">
                <thead>
                  <tr>
                    <th>Thumbnail</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Author</th>
                    <th>Languages</th>
                    <th>Status</th>
                    <th>Views</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(article => (
                    <tr key={article.id}>
                      <td>
                        {article.thumbnailURL
                          ? <img src={article.thumbnailURL} alt="" className="article-thumb" />
                          : <div className="article-thumb-ph">📰</div>
                        }
                      </td>
                      <td className="article-title-cell">
                        <span title={article.title}>{article.title}</span>
                      </td>
                      <td><span className="badge" style={{ fontSize: '0.68rem' }}>{article.category}</span></td>
                      <td className="article-author">{article.author}</td>
                      <td>
                        <div className="article-lang-badges">
                          {article.languages
                            ? article.languages.map(l => (
                              <span key={l} className={`lang-badge lang-badge-${l}`}>
                                {l === 'english' ? '🇬🇧' : '🇮🇳'} {l.charAt(0).toUpperCase() + l.slice(1, 3)}
                              </span>
                            ))
                            : <span className="lang-badge lang-badge-english">🇬🇧 En</span>
                          }
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${article.status}`}>
                          {article.status === 'published' ? '✅ Live' : '📝 Draft'}
                        </span>
                      </td>
                      <td className="article-views">👁 {article.viewCount || 0}</td>
                      <td className="article-date">{formatDateTime(article.createdAt)}</td>
                      <td>
                        <div className="article-actions">
                          <Link to={`/admin/edit-article/${article.id}`} className="btn btn-dark btn-sm">Edit</Link>
                          <button className={`btn btn-sm ${article.status === 'published' ? 'btn-secondary' : 'btn-success'}`}
                            onClick={() => handleToggleStatus(article)}>
                            {article.status === 'published' ? 'Unpublish' : 'Publish'}
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(article)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      }
    </div>
  );
}